'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Link from 'next/link';

export function HeroSection() {
  const [mounted, setMounted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const fadeOpacity = useTransform(scrollYProgress, [0.5, 1], [0, 1]);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="snap-start relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Mountain background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=3200")',
        }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#050A15]/45" />

      {/* Scroll-driven fade to white */}
      <motion.div
        style={{ opacity: fadeOpacity }}
        className="absolute inset-0 bg-card pointer-events-none z-10"
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6 text-center max-w-[1100px] mx-auto w-full">
        {/* YC Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex justify-center mb-10"
        >
          <div className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full border border-white/15 dark:border-white/10 bg-card/5 backdrop-blur-sm">
            <span
              className="text-[15px] text-white/70"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              Backed by
            </span>
            <div className="w-5 h-5 bg-[#FF6600] flex items-center justify-center">
              <span className="text-body-sm font-bold text-white leading-none">Y</span>
            </div>
            <span
              className="text-[15px] text-white/70"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              Combinator
            </span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="tracking-tight leading-[1.05] mb-6 text-white"
          style={{
            fontFamily: 'Nunito, sans-serif',
            fontSize: 'clamp(2.75rem, 6vw + 1rem, 5.5rem)',
            fontWeight: 300,
          }}
        >
          Production Monitoring
          <br />
          <span className="italic text-white/80">for AI Agents</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-white/90 max-w-xl mx-auto leading-relaxed mb-10 text-lg sm:text-xl"
          style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 300 }}
        >
          Find false claims of completion, inspect the tool evidence, and compare what changed.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-6"
        >
          <Link
            href="/demo/insurance"
            className="px-8 py-3.5 bg-warm-brown text-white text-[15px] font-medium hover:bg-warm-brown-hover transition-all duration-200"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Explore an example →
          </Link>
          <a
            href="/docs/quickstart"
            className="px-8 py-3.5 border border-white/25 dark:border-white/10 text-white text-[15px] font-medium hover:border-white/40 dark:hover:border-white/10 hover:bg-card/5 transition-all duration-200"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Connect your agent
          </a>
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={mounted ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-white/60 text-body-sm"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Recorded agent runs. No account needed to explore.
        </motion.p>
      </div>
    </section>
  );
}
